<template>
    <Teleport to="body">
        <transition name="dialog">
            <div v-if="dialog" class="dialog-host" @mousedown.self="cancel">
                <div class="dialog-host__backdrop" aria-hidden="true"></div>
                <div
                    ref="panel"
                    role="dialog"
                    aria-modal="true"
                    :aria-labelledby="titleId"
                    :aria-describedby="messageId"
                    class="dialog-host__panel"
                    @keydown="handleKeydown"
                >
                    <div class="flex items-start gap-4 px-6 pt-6">
                        <div :class="iconClass" class="dialog-host__icon">
                            <svg v-if="dialog.tone === 'danger' || dialog.tone === 'error'" class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"/></svg>
                            <svg v-else-if="dialog.tone === 'success'" class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                            <svg v-else class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                        </div>
                        <div class="min-w-0 flex-1">
                            <h2 :id="titleId" class="text-lg font-bold text-on-surface">{{ dialog.title }}</h2>
                            <p :id="messageId" class="mt-1 whitespace-pre-line text-sm leading-6 text-on-surface-variant">{{ dialog.message }}</p>
                        </div>
                        <button type="button" class="-mr-2 -mt-2 rounded-xl p-2 text-outline transition-colors hover:bg-surface-container hover:text-on-surface" aria-label="Cerrar" @click="cancel">
                            <span class="material-symbols-outlined text-xl">close</span>
                        </button>
                    </div>
                    <div v-if="$slots.body" class="px-6 pt-5">
                        <slot name="body" :dialog="dialog"></slot>
                    </div>
                    <div v-else-if="dialog.kind === 'prompt'" class="px-6 pt-5">
                        <textarea
                            ref="input"
                            v-model="inputValue"
                            :placeholder="dialog.placeholder"
                            rows="4"
                            class="input-modern w-full resize-none"
                            @keydown.ctrl.enter="accept"
                        ></textarea>
                    </div>
                    <div class="dialog-host__footer">
                        <slot name="footer" :accept="accept" :cancel="cancel" :dialog="dialog">
                            <button v-if="dialog.kind !== 'alert'" ref="cancelButton" type="button" class="dialog-host__btn-secondary" @click="cancel">
                                {{ dialog.cancelText }}
                            </button>
                            <!--
                                `id` y no solo `ref`: un `ref` vive en el
                                componente Vue y no existe en el DOM, así que
                                desde Selenium no hay forma de llegar a este
                                botón. Con el id, cualquier prueba de navegador
                                puede confirmar un diálogo. Es el mismo motivo
                                por el que `Input.vue` lleva id explicito.
                            -->
                            <button id="dialogo-confirmar" ref="confirmButton" type="button" :class="['dialog-host__btn-primary', confirmClass]" :disabled="dialog.kind === 'prompt' && dialog.required && !inputValue.trim()" @click="accept">
                                {{ dialog.confirmText }}
                            </button>
                        </slot>
                    </div>
                </div>
            </div>
        </transition>
    </Teleport>
</template>

<script>
export default {
    data() {
        return {
            dialog: null,
            inputValue: '',
            resolveDialog: null,
            previousFocus: null,
            titleId: 'nido-dialog-title',
            messageId: 'nido-dialog-message',
        };
    },
    computed: {
        iconClass() {
            const map = {
                danger: 'bg-error-container text-error',
                error: 'bg-error-container text-error',
                warning: 'bg-tertiary-container text-tertiary-container',
                success: 'bg-primary-container text-on-primary-container',
                info: 'bg-secondary-container text-on-secondary-container',
            };
            return map[this.dialog?.tone] || map.info;
        },
        confirmClass() {
            if (this.dialog?.tone === 'danger' || this.dialog?.tone === 'error') return 'bg-error text-on-error';
            if (this.dialog?.tone === 'success') return 'bg-primary text-on-primary';
            if (this.dialog?.tone === 'warning') return 'bg-tertiary text-on-tertiary';
            return 'bg-primary text-on-primary';
        },
    },
    methods: {
        open(event) {
            if (this.resolveDialog) this.resolveDialog(this.dialog?.kind === 'prompt' ? null : false);
            const detail = event.detail || {};
            this.previousFocus = document.activeElement;
            this.inputValue = detail.value || '';
            this.dialog = {
                kind: detail.kind || 'alert',
                title: detail.title || (detail.kind === 'confirm' ? 'Confirmar acción' : detail.kind === 'prompt' ? 'Ingresa la información' : 'Aviso'),
                message: detail.message || '',
                tone: detail.tone || (detail.kind === 'confirm' ? 'warning' : 'info'),
                confirmText: detail.confirmText || (detail.kind === 'alert' ? 'Entendido' : 'Continuar'),
                cancelText: detail.cancelText || 'Cancelar',
                placeholder: detail.placeholder || '',
                required: detail.required ?? false,
            };
            this.resolveDialog = detail.resolve || null;
            document.body.style.overflow = 'hidden';
            this.$nextTick(() => {
                const target = this.dialog.kind === 'prompt' ? this.$refs.input : this.$refs.confirmButton;
                target?.focus();
            });
        },
        accept() {
            if (this.dialog?.kind === 'prompt' && this.dialog.required && !this.inputValue.trim()) return;
            this.close(this.dialog?.kind === 'prompt' ? this.inputValue : true);
        },
        cancel() {
            this.close(this.dialog?.kind === 'prompt' ? null : false);
        },
        close(result) {
            const resolve = this.resolveDialog;
            this.dialog = null;
            this.resolveDialog = null;
            this.inputValue = '';
            document.body.style.overflow = '';
            resolve?.(result);
            this.$nextTick(() => this.previousFocus?.focus?.());
        },
        handleKeydown(event) {
            if (event.key === 'Escape') {
                event.preventDefault();
                this.cancel();
                return;
            }
            if (event.key !== 'Tab') return;
            const focusable = [...this.$refs.panel.querySelectorAll('button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')];
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        },
    },
    mounted() {
        window.addEventListener('nido:dialog', this.open);
    },
    beforeUnmount() {
        window.removeEventListener('nido:dialog', this.open);
        document.body.style.overflow = '';
    },
};
</script>

<style scoped>
.dialog-host {
    position: fixed;
    inset: 0;
    z-index: 250;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
}
.dialog-host__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.40);
    backdrop-filter: blur(8px) saturate(150%);
    -webkit-backdrop-filter: blur(8px) saturate(150%);
}
.dialog-host__panel {
    position: relative;
    width: 100%;
    max-width: 420px;
    overflow: hidden;
    border-radius: 28px;
    border: 1px solid rgba(255, 255, 255, 0.7);
    background: #ffffff;
    box-shadow:
        0 20px 25px -5px rgba(0, 0, 0, 0.10),
        0 8px 10px -6px rgba(0, 0, 0, 0.08),
        inset 0 1px 0 rgba(255, 255, 255, 0.6);
}
.dialog-host__icon {
    flex-shrink: 0;
    display: flex;
    width: 44px;
    height: 44px;
    align-items: center;
    justify-content: center;
    border-radius: 16px;
}
.dialog-host__footer {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.5);
    background: rgba(255, 255, 255, 0.4);
    backdrop-filter: blur(8px);
    padding: 16px 24px;
}
.dialog-host__btn-secondary {
    border-radius: 9999px;
    padding: 10px 20px;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--color-m3-on-surface-variant);
    background: transparent;
    transition: background 150ms ease;
}
.dialog-host__btn-secondary:hover {
    background: var(--color-m3-surface-container-high);
    color: var(--color-m3-on-surface);
}
.dialog-host__btn-primary {
    border-radius: 9999px;
    padding: 10px 24px;
    font-size: 0.875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.01em;
    color: var(--color-m3-on-primary);
    background: var(--color-m3-primary);
    transition: transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 200ms ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.dialog-host__btn-primary:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.10);
}
.dialog-host__btn-primary:active {
    transform: scale(0.97);
}
.dialog-enter-active, .dialog-leave-active { transition: opacity 220ms cubic-bezier(0.2, 0, 0, 1); }
.dialog-enter-active > div:last-child, .dialog-leave-active > div:last-child {
    transition: transform 320ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 220ms ease;
}
.dialog-enter-from, .dialog-leave-to { opacity: 0; }
.dialog-enter-from > div:last-child, .dialog-leave-to > div:last-child {
    transform: translateY(12px) scale(0.96);
    opacity: 0;
}
</style>
